/*
 * Lumeer: Modern Data Definition and Processing Platform
 *
 * Copyright (C) since 2017 Lumeer.io, s.r.o. and/or its affiliates.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {ConstraintData} from '@lumeer/data-filters';
import {Attribute} from '../../../../core/store/collections/collection';
import {AllowedPermissions} from '../../../../core/model/allowed-permissions';
import {DataRow, DataRowService} from '../../../data/data-row.service';
import {Query} from '../../../../core/store/navigation/query/query';
import {DataResourceDataRowComponent} from './row/data-resource-data-row.component';
import {filterUnusedAttributes} from '../../../utils/attribute.utils';
import {HiddenInputComponent} from '../../../input/hidden-input/hidden-input.component';
import {DataRowFocusService} from '../../../data/data-row-focus-service';
import {BehaviorSubject, Subscription} from 'rxjs';
import {Workspace} from '../../../../core/store/navigation/workspace';
import {AttributesResource, AttributesResourceType, DataResource} from '../../../../core/model/resource';
import {View} from '../../../../core/store/views/view';
import {objectChanged} from '../../../utils/common.utils';
import {User} from '../../../../core/store/users/user';
import {ResourceAttributeSettings} from '../../../../core/store/view-settings/view-settings';

@Component({
  selector: 'data-resource-data',
  templateUrl: './data-resource-data.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DataRowService],
})
export class DataResourceDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public resource: AttributesResource;

  @Input()
  public dataResource: DataResource;

  @Input()
  public resourceType: AttributesResourceType;

  @Input()
  public constraintData: ConstraintData;

  @Input()
  public permissions: AllowedPermissions;

  @Input()
  public query: Query;

  @Input()
  public workspace: Workspace;

  @Input()
  public user: User;

  @Input()
  public view: View;

  @Input()
  public toolbarRef: TemplateRef<any>;

  @Input()
  public preventEventBubble: boolean;

  @Input()
  public editableKeys = false;

  @Input()
  public editableValues = true;

  @Input()
  public attributeSettings: ResourceAttributeSettings[];

  @Output()
  public attributeTypeClick = new EventEmitter<Attribute>();

  @Output()
  public attributeFunctionCLick = new EventEmitter<Attribute>();

  @ViewChildren(DataResourceDataRowComponent)
  public rows: QueryList<DataResourceDataRowComponent>;

  @ViewChild(HiddenInputComponent)
  public hiddenInputComponent: HiddenInputComponent;

  @Output()
  public switchToTable = new EventEmitter();

  @Output()
  public removeDocument = new EventEmitter();

  @Output()
  public dataResourceChanged = new EventEmitter<DataResource>();

  public unusedAttributes$ = new BehaviorSubject<Attribute[]>([]);

  private dataRowFocusService: DataRowFocusService;
  private subscriptions = new Subscription();

  constructor(public dataRowService: DataRowService) {
    this.dataRowFocusService = new DataRowFocusService(
      () => 2,
      () => this.dataRowService.rows$.value.length,
      () => this.rows.toArray(),
      () => this.hiddenInputComponent,
      (row, column) => this.dataRowService.rows$.value[row]?.attribute?.constraint?.isDirectlyEditable
    );
  }

  public ngOnInit() {
    const subscription = this.dataRowService.rows$.subscribe(() => {
      const currentDataResource = this.getCurrentDataResource();
      const unusedAttributes = filterUnusedAttributes(this.resource?.attributes, currentDataResource?.data);
      this.unusedAttributes$.next(unusedAttributes);
    });
    this.subscriptions.add(subscription);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (this.shouldRefreshObservables(changes)) {
      this.dataRowService.init(this.resource, this.dataResource, this.attributeSettings);
    } else if (changes.attributeSettings || changes.permissions) {
      this.dataRowService.setSettings(this.attributeSettings);
    }
    if (changes.workspace) {
      this.dataRowService.setWorkspace(this.workspace);
    }
    if (objectChanged(changes.dataResource)) {
      this.focusFirstDataInput();
    }
  }

  private focusFirstDataInput() {
    if (!this.dataRowFocusService.isFocusing() && !this.dataRowFocusService.isEditing()) {
      setTimeout(() => this.rows?.first?.onValueFocus());
    }
  }

  private shouldRefreshObservables(changes: SimpleChanges): boolean {
    if (this.resource && this.dataResource) {
      if (this.dataResource.id) {
        return objectChanged(changes.resource) || objectChanged(changes.dataResource);
      } else {
        return !!changes.resource || !!changes.dataResource;
      }
    }
    return false;
  }

  public onNewKey(value: string, index: number) {
    this.dataRowService.updateRow(index, value);
  }

  public onNewValue(value: any, row: DataRow, index: number) {
    this.dataRowService.updateRow(index, null, value);
    this.emitDataChange();
  }

  private emitDataChange() {
    const currentDataResource = this.getCurrentDataResource();
    this.dataResourceChanged.emit(currentDataResource);
  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.dataRowService.destroy();
  }

  public onRemoveRow(index: number) {
    this.dataRowService.deleteRow(index);
  }

  public onAttributeFunction(row: DataRow) {
    if (row.attribute) {
      this.attributeFunctionCLick.emit(row.attribute);
    }
  }

  public onAttributeType(row: DataRow) {
    if (row.attribute) {
      this.attributeTypeClick.emit(row.attribute);
    }
  }

  public onFocus(row: number, column: number) {
    this.dataRowFocusService.focus(row, this.editableKeys ? column : 1);
  }

  public onResetFocusAndEdit(row: number, column: number) {
    this.dataRowFocusService.resetFocusAndEdit(row, this.editableKeys ? column : 1);
  }

  public onEdit(row: number, column: number) {
    this.dataRowFocusService.edit(row, this.editableKeys ? column : 1);
  }

  @HostListener('document:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent) {
    this.dataRowFocusService.onKeyDown(event, {column: !this.editableKeys});
  }

  public trackByRow(index: number, row: DataRow): string {
    return row.id;
  }

  public onNewHiddenInput(value: string) {
    this.dataRowFocusService.newHiddenInput(value);
  }

  private getCurrentDataResource(): DataResource {
    if (!this.dataResource) {
      return null;
    }

    const rows = this.dataRowService.rows$.value;

    const data = rows
      .filter(row => row.attribute && row.attribute.id)
      .reduce((d, row) => {
        if (row.attribute.constraint) {
          d[row.attribute.id] = row.attribute.constraint.createDataValue(row.value, this.constraintData).serialize();
        } else {
          d[row.attribute.id] = row.value;
        }
        return d;
      }, {});

    const currentAttributeNames = (this.resource?.attributes || []).map(attr => attr.name);
    const newData = rows
      .filter(row => row.key && (!row.attribute || !row.attribute.id) && !currentAttributeNames.includes(row.key))
      .reduce(
        (d, row) => ({
          ...d,
          [row.key]: row.value,
        }),
        {}
      );

    return {...this.dataResource, data, newData};
  }
}
